// process-liveness-probe.ts
//
// Tách khỏi mission-lock.ts (giữ mỗi file ≤200 dòng): file này chỉ trả lời ba câu hỏi
// về HỆ ĐIỀU HÀNH, không biết gì về lock/mission.
//
//   isAlive(pid)          — tiến trình còn sống không?
//   processStartedAt(pid) — mốc khởi động, dùng để phát hiện PID TÁI SỬ DỤNG.
//   descendantPids(pid)   — con trực tiếp, để biết "supervisor chết nhưng con còn sống".
//
// Vì sao cần start-time: PID bị hệ điều hành cấp lại. Chỉ hỏi "pid còn sống không" thì
// một PID đã tái sử dụng bởi tiến trình HOÀN TOÀN KHÁC sẽ bị hiểu nhầm là chủ lock cũ
// vẫn chạy (hoặc tệ hơn — bị coi là chết và bị cướp lock). macOS không có /proc nên
// dùng `ps -o lstart=`; Linux cũng hỗ trợ cờ này nên một đường chạy chung cho cả hai.

import { spawnAsyncCapture } from "../spawn-async-capture-with-timeout.js";

export interface LockLivenessProbe {
  isAlive: (pid: number) => Promise<boolean>;
  processStartedAt: (pid: number) => Promise<string | undefined>;
  descendantPids: (pid: number) => Promise<number[]>;
}

const PROBE_TIMEOUT_MS = 5_000;

export const realLivenessProbe: LockLivenessProbe = {
  isAlive: async (pid) => {
    try {
      process.kill(pid, 0);
      return true;
    } catch (err) {
      // EPERM = tiến trình TỒN TẠI nhưng thuộc user khác ⇒ vẫn là "còn sống".
      // Chỉ ESRCH mới là "không có tiến trình nào".
      return (err as NodeJS.ErrnoException).code === "EPERM";
    }
  },
  processStartedAt: async (pid) => {
    const res = await spawnAsyncCapture("ps", ["-o", "lstart=", "-p", String(pid)], {
      timeoutMs: PROBE_TIMEOUT_MS,
      forwardSigint: false,
    }).catch(() => undefined);
    const text = res?.stdout.trim();
    return text ? text : undefined;
  },
  descendantPids: async (pid) => {
    const res = await spawnAsyncCapture("pgrep", ["-P", String(pid)], {
      timeoutMs: PROBE_TIMEOUT_MS,
      forwardSigint: false,
    }).catch(() => undefined);
    if (!res?.stdout) return [];
    return res.stdout
      .split("\n")
      .map((line) => Number(line.trim()))
      .filter((n) => Number.isInteger(n) && n > 0);
  },
};

/**
 * 06g R2b (DRY with orphaned-supervisor-lock-release.ts): dead OR pid reused
 * ⇒ true ("this process cannot still be the one recorded"). A missing
 * recorded start time only proves death when the pid is gone.
 */
export async function deadOrPidReused(
  liveness: LockLivenessProbe,
  pid: number,
  recordedStart: string | undefined,
): Promise<boolean> {
  if (!(await liveness.isAlive(pid))) return true;
  const now = await liveness.processStartedAt(pid);
  return recordedStart !== undefined && now !== undefined && now !== recordedStart;
}
